import { useEffect } from 'react'
import i18n from 'i18next'
import { hexToRgbComponents, darkenHex } from '@/utils/colorUtils'
import { useTenantStore } from '@/store/tenant'
import { useAuthStore } from '@/store/auth'
import type { TenantTheme } from '@/types/tenant'

/** Maps the borderRadius token to a CSS rem value */
const RADIUS_MAP: Record<NonNullable<TenantTheme['borderRadius']>, string> = {
  none: '0',
  sm:   '0.25rem',
  md:   '0.375rem',
  lg:   '0.5rem',
  full: '9999px',
}

/**
 * Inject a Google Fonts stylesheet for the given font family.
 * Safe to call multiple times — de-duplicates via data-font attribute.
 */
function loadGoogleFont(family: string): void {
  if (!family || family.toLowerCase().startsWith('system') || family.toLowerCase().startsWith('ui-')) {
    return
  }
  const existing = document.querySelector(`link[data-font="${family}"]`)
  if (existing) return

  // Remove any previously injected tenant font
  document.querySelector('link[data-tenant-font]')?.remove()

  const encoded = family.trim().replace(/ /g, '+')
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.setAttribute('data-font', family)
  link.setAttribute('data-tenant-font', 'true')
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700&display=swap`
  document.head.appendChild(link)
}

/**
 * Apply a TenantTheme to the DOM by setting CSS custom properties on <html>.
 * This is called synchronously from the cache (pre-auth) and
 * asynchronously after the settings API call resolves (post-auth).
 */
export function applyThemeToDom(theme: TenantTheme): void {
  const root = document.documentElement

  // ── Primary brand color ─────────────────────────────────────────────────
  root.style.setProperty('--color-primary', hexToRgbComponents(theme.primaryColor))
  root.style.setProperty('--color-primary-fg', hexToRgbComponents(theme.primaryForeground))
  root.style.setProperty(
    '--color-primary-hover',
    hexToRgbComponents(darkenHex(theme.primaryColor, 8))
  )

  // ── Geometry ─────────────────────────────────────────────────────────────
  root.style.setProperty('--radius', RADIUS_MAP[theme.borderRadius] ?? RADIUS_MAP.md)
  root.style.setProperty('--radius-sm', `calc(${RADIUS_MAP[theme.borderRadius] ?? RADIUS_MAP.md} * 0.67)`)
  root.style.setProperty('--radius-lg', `calc(${RADIUS_MAP[theme.borderRadius] ?? RADIUS_MAP.md} * 1.5)`)

  // ── Font ──────────────────────────────────────────────────────────────────
  if (theme.fontFamily) {
    loadGoogleFont(theme.fontFamily)
    const fontStack = `"${theme.fontFamily}", ui-sans-serif, system-ui, sans-serif`
    root.style.setProperty('--font-sans', fontStack)
    root.style.setProperty('--font-heading', fontStack)
  }

  // ── Direction / language ──────────────────────────────────────────────────
  root.setAttribute('dir', theme.direction)
  root.setAttribute('lang', theme.locale)
  // Tailwind's `rtl:` variant is activated by the `dir` attribute
  // but some libraries (e.g. Floating UI) check for the `rtl` class
  root.classList.toggle('rtl', theme.direction === 'rtl')

  // ── Favicon ───────────────────────────────────────────────────────────────
  if (theme.faviconUrl) {
    const favicon = document.getElementById('favicon') as HTMLLinkElement | null
    if (favicon) favicon.href = theme.faviconUrl
  }

  // ── App name (<title>) ────────────────────────────────────────────────────
  if (theme.appName) {
    const titleEl = document.getElementById('app-title')
    if (titleEl) titleEl.textContent = theme.appName
    // Also update document.title directly for SPAs that set it on page navigation
    if (!document.title.endsWith(theme.appName)) {
      document.title = theme.appName
    }
  }
}

/**
 * ThemeProvider
 *
 * Responsibilities:
 * 1. On mount: load cached branding from localStorage and apply immediately
 *    (so the login page shows tenant colours without a network round-trip).
 * 2. After auth: watch for theme changes in the tenant store and re-apply.
 * 3. Sync i18n language with theme.locale whenever it changes.
 *
 * Does NOT render any DOM nodes — purely a side-effect container.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useTenantStore((s) => s.theme)
  const loadFromCache = useTenantStore((s) => s.loadFromCache)
  const fetchPublicBranding = useTenantStore((s) => s.fetchPublicBranding)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Step 1a: Synchronous cache restore (shows cached branding instantly)
  // Step 1b: Async fetch of public branding (updates login page with fresh domain name/logo)
  useEffect(() => {
    loadFromCache()
    fetchPublicBranding()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Step 2: Apply theme to DOM whenever it changes
  useEffect(() => {
    if (theme) {
      applyThemeToDom(theme)
    }
  }, [theme])

  // Step 3: Sync i18n language with theme locale — only as a first-run default.
  // If the user has already explicitly chosen a language (stored in localStorage),
  // their choice ALWAYS wins over the tenant's default locale.
  useEffect(() => {
    if (!theme) return
    const userPicked = localStorage.getItem('i18n_language')
    if (userPicked) return          // user preference takes precedence — do not override
    const current = i18n.language?.split('-')[0]
    const target  = theme.locale.split('-')[0]
    if (current !== target) {
      i18n.changeLanguage(theme.locale).catch(console.error)
    }
  }, [theme?.locale])

  // Step 4: After auth, trigger settings fetch (handled by useSettings hook in AppLayout)
  // Nothing to do here — the hook is mounted inside the authenticated layout.
  void isAuthenticated

  return <>{children}</>
}
